import React, { useMemo } from 'react'

//
import { SiteDataContext } from '../../../components/page-wrapper'
import { LoadMore, Articles } from '../../../storybook'
import { getMicroCopy } from '../../../utils/microcopy'
import { useMicroCopyData } from '@/src/context/MicroCopyContext'
import { Image as DatoImage } from 'react-datocms'

// Component to add structured data for SEO
const ArticleStructuredData = ({ articles, slugPrefix }) => {
  const structuredData = useMemo(() => {
    if (!articles || articles.length === 0) return null

    const articleList = articles.map((article) => ({
      '@type': 'Article',
      '@id': `${slugPrefix}blog/${article?.categories?.[0]?.slug}/${article.slug}`,
      headline: article.title,
      description: article.description,
      image: article?.image?.url,
      datePublished: article.date,
      dateModified: article.date,
      author: {
        '@type': 'Organization',
        name: 'Deliverect'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Deliverect',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.deliverect.com/logo.png' // Update with actual logo URL
        }
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${slugPrefix}blog/${article?.categories?.[0]?.slug}/${article.slug}`
      }
    }))

    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: articleList.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: article
      }))
    }
  }, [articles, slugPrefix])

  if (!structuredData) return null

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  )
}

const ArticlesListing = ({ articles, slugPrefix = '/', limit = 6 }) => {
  const microCopyData = useMicroCopyData()
  const { lang } = React.useContext(SiteDataContext)
  const [offsetToShow, setOffsetToShow] = React.useState(limit)

  // Memoize the processed articles to avoid recreating objects on every render
  const processedArticles = useMemo(() => {
    if (!articles) return []

    return articles.slice(0, offsetToShow).map((item) => ({
      id: item?.id || '',
      eyebrow: item?.categories?.map((cat) => cat.name)?.join(', '),
      image: (
        <DatoImage
          data={item.image.responsiveImage || item.image}
          imgStyle={{
            objectFit: 'cover'
          }}
        />
      ),
      video: item?.video,
      title: item?.title,
      description: item?.description,
      readtime: getMicroCopy({
        key: `readingTime`,
        data: microCopyData[lang],
        tokens: { min: item?.readingTime || '{min}' }
      }),
      date: item?.date,
      url: `${slugPrefix}blog/${item?.categories?.[0]?.slug}/${item.slug}`.replace(/\/{2,}/gim, '/')
    }))
  }, [articles, offsetToShow, microCopyData, lang, slugPrefix])

  return (
    <>
      {/* Add structured data for SEO */}
      <ArticleStructuredData articles={articles} slugPrefix={slugPrefix} />

      <Articles
        articles={processedArticles}
        footerSlot={
          offsetToShow < articles?.length && (
            <div className="l-section__footer">
              <LoadMore
                current={offsetToShow}
                total={articles?.length || 0}
                loadLabel={getMicroCopy({
                  key: `paginationLoadMore`,
                  data: microCopyData[lang]
                })}
                separationLabel={getMicroCopy({
                  key: `paginationOf`,
                  data: microCopyData[lang]
                })}
                handleLoadMore={() => setOffsetToShow(offsetToShow + limit)}
              />
            </div>
          )
        }
        classes={'l-section--grey'}
      />
    </>
  )
}

export default ArticlesListing
